import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { FiLink, FiEdit2, FiStar, FiMoreVertical, FiExternalLink } from 'react-icons/fi';
import useCoordinatedDropdown from '../../hooks/useCoordinatedDropdown';
import { standardMenuSurface, standardMenuItem } from '../../appearance/menuStyles';
import { WHITE } from '../../appearance/Colors';
import { QUERY, MUTATION } from '../../gql-operations';

export const SaveFolderReferenceButton = ({ token, folderId, buttonComponent = LinkButton, menuItem = false, onAction }) => {
    const { t } = useTranslation();
    const history = useHistory();
    const authenticated = Boolean(localStorage.getItem('token'));
    const { data, loading, error } = useQuery(QUERY.GET_FOLDER_REFERENCES, { skip: !authenticated, pollInterval: 30000 });
    const [status, setStatus] = useState('');
    const [save, { loading: saving }] = useMutation(MUTATION.SET_FOLDER_REFERENCE, {
        refetchQueries: [{ query: QUERY.GET_FOLDER_REFERENCES }], awaitRefetchQueries: true
    });
    const Button = buttonComponent;
    const pinned = (data?.getFolderReferences || []).some(reference => token ? reference.token === token : !reference.token && reference.folderId === folderId);
    return <>
        <Button as="button" type="button" role={menuItem ? 'menuitem' : undefined} disabled={saving || (authenticated && (loading || Boolean(error)))} aria-pressed={pinned}
            onClick={async event => {
                if (menuItem) event.stopPropagation();
                if (!authenticated) { history.push(`/login?redirect=${encodeURIComponent(`/s/${token}`)}`); return; }
                setStatus('');
                try { await save({ variables: { data: { ...(token ? { token } : { folderId }), pinned: !pinned } } }); onAction?.(); }
                catch (_) { setStatus(t('folders:linkActionFailed')); }
            }}>
            <FiLink size={15} />{pinned ? t('folders:removeSavedLink') : t('folders:saveFolderLink')}
        </Button>
        {status ? <span role="alert">{status}</span> : null}
    </>;
};

export const FavoriteFolderReferenceButton = ({ folderId, buttonComponent = LinkButton, onAction }) => {
    const { t } = useTranslation();
    const { data, loading, error } = useQuery(QUERY.GET_FOLDER_REFERENCES, { pollInterval: 30000 });
    const [status, setStatus] = useState('');
    const options = { refetchQueries: [{ query: QUERY.GET_FOLDER_REFERENCES }], awaitRefetchQueries: true };
    const [save, { loading: saving }] = useMutation(MUTATION.SET_FOLDER_REFERENCE, options);
    const [setFavorite, { loading: favoriting }] = useMutation(MUTATION.SET_FOLDER_REFERENCE_FAVORITE, options);
    const reference = (data?.getFolderReferences || []).find(item => !item.token && item.folderId === folderId);
    const Button = buttonComponent;
    return <>
        <Button as="button" type="button" role="menuitem" disabled={loading || Boolean(error) || saving || favoriting}
            onClick={async event => {
                event.stopPropagation();
                try {
                    setStatus('');
                    if (reference) await setFavorite({ variables: { referenceId: reference.id, favorite: !reference.isFavorite } });
                    else await save({ variables: { data: { folderId, pinned: true, favorite: true } } });
                    onAction?.();
                } catch (_) { setStatus(t('folders:linkActionFailed')); }
            }}>
            <FiStar size={15} />{reference?.isFavorite ? t('folders:removeSavedLinkFavorite') : t('folders:addSavedLinkFavorite')}
        </Button>
        {status ? <span role="alert">{status}</span> : null}
    </>;
};

export default function FolderLinkActions({ token, linkRole, onOpenInApp }) {
    const { t } = useTranslation();
    const history = useHistory();
    const [status, setStatus] = useState('');
    const [open, setOpen] = useCoordinatedDropdown();
    const anchor = React.useRef(null);
    const trigger = React.useRef(null);
    const [join, { loading }] = useMutation(MUTATION.JOIN_PUBLISHED_FOLDER);
    React.useEffect(() => {
        if (!open) return;
        const outside = event => { if (!anchor.current?.contains(event.target)) setOpen(false); };
        const escape = event => { if (event.key === 'Escape') { setOpen(false); trigger.current?.focus(); } };
        document.addEventListener('click', outside);
        document.addEventListener('keydown', escape);
        return () => { document.removeEventListener('click', outside); document.removeEventListener('keydown', escape); };
    }, [open, setOpen]);
    return <ActionsAnchor ref={anchor}>
        <MoreButton ref={trigger} type="button" aria-label={t('folders:moreActions')} aria-haspopup="menu" aria-expanded={open}
            onClick={() => setOpen(current => !current)}><FiMoreVertical size={18} /></MoreButton>
        {open ? <ActionMenu role="menu" aria-label={t('folders:moreActions')}>
            <SaveFolderReferenceButton token={token} buttonComponent={ActionItem} menuItem onAction={() => setOpen(false)} />
            {onOpenInApp ? <ActionItem type="button" role="menuitem" onClick={() => { setOpen(false); onOpenInApp(); }}>
                <FiExternalLink size={15} />{t('messages:openInApp')}</ActionItem> : null}
            {['ADD', 'EDIT'].includes(linkRole) ? <ActionItem type="button" role="menuitem" disabled={loading} onClick={async () => {
                if (!localStorage.getItem('token')) { setOpen(false); history.push(`/login?redirect=${encodeURIComponent(`/s/${token}`)}`); return; }
                try {
                    setStatus('');
                    const result = await join({ variables: { token } });
                    setOpen(false);
                    history.push(`/f/${result.data.joinPublishedFolder}`);
                } catch (_) { setStatus(t('folders:linkActionFailed')); }
            }}><FiEdit2 size={15} />{t('folders:openCollaborativeFolder')}</ActionItem> : null}
            {status ? <span role="alert">{status}</span> : null}
        </ActionMenu> : null}
    </ActionsAnchor>;
}
const ActionsAnchor = styled.div`position: relative; display: inline-flex;`;
const MoreButton = styled.button`
    color: ${WHITE}; background: transparent; border: 0; border-radius: 8px;
    display: inline-flex; align-items: center; justify-content: center; padding: 10px; cursor: pointer;
    &:hover { background: rgba(255,255,255,.08); }
    &:focus-visible { outline: 2px solid ${WHITE}; }
`;
const ActionMenu = styled.div`
    ${standardMenuSurface}; position: absolute; top: calc(100% + 8px); right: 0;
    min-width: 220px; max-width: calc(100vw - 24px); z-index: 1500;
`;
const ActionItem = styled.button`${standardMenuItem};`;

const LinkButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 7px;
    border-radius: 9px;
    border: 1px solid rgba(255,255,255,.14);
    background: rgba(255,255,255,.06);
    color: ${WHITE};
    padding: 8px 10px;
    font: inherit;
    cursor: pointer;
    &:hover { background: rgba(255,255,255,.12); }
    &:disabled { opacity: .45; cursor: default; }
`;
